import { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { useOrg } from '@/contexts/OrgContext';
import { usePermissions } from '@/hooks/usePermissions';
import { useViewHealthMarker } from '@/lib/view-health';

import { ConnectHubspotButton } from './components/ConnectHubspotButton';
import { ConnectionStatusPanel } from './components/ConnectionStatusPanel';
import { HubspotOauthErrorBanner } from './components/HubspotOauthErrorBanner';
import { HubspotSetupPanel } from './components/HubspotSetupPanel';
import { useHubspotStatus } from './hooks/useHubspotStatus';
import { HubspotIcon } from './HubspotIcon';
import { PipelineStageMappingManager } from './PipelineStageMappingManager';

export function HubspotSection() {
  const { currentOrg } = useOrg();
  const orgId = currentOrg?.id;
  const { hasPermissionGroup } = usePermissions();
  const isAdmin = hasPermissionGroup('ahoy_other_admin');
  useViewHealthMarker('settings-section-ready', { ready: true });

  const [searchParams, setSearchParams] = useSearchParams();
  const status = useHubspotStatus(orgId);
  const { refetch: refetchStatus } = status;

  const [setupDone, setSetupDone] = useState(false);

  useEffect(() => {
    if (searchParams.get('hubspot_oauth') === 'success') {
      setSearchParams(
        prev => {
          const nextParams = new URLSearchParams(prev);
          nextParams.delete('hubspot_oauth');
          return nextParams;
        },
        { replace: true }
      );
    }
  }, [searchParams, setSearchParams]);

  const handleFinished = useCallback(() => {
    setSetupDone(true);
    void refetchStatus();
  }, [refetchStatus]);

  const connected = status.data?.connected ?? false;
  const bulkDone = !!status.data?.bulk_completed_at;
  const isComplete = connected && (bulkDone || setupDone);

  return (
    <div className='space-y-8'>
      <header>
        <div className='flex items-center gap-2'>
          <HubspotIcon className='h-5 w-5 text-[var(--color-text-primary)]' />
          <h2 className='text-lg font-semibold text-fg-primary'>HubSpot</h2>
        </div>
        <p className='mt-0.5 text-sm text-fg-tertiary'>
          Sync your HubSpot contacts, companies, and deals with Ahoy.
        </p>
      </header>

      <div className='space-y-6'>
        <HubspotOauthErrorBanner />

        {!isAdmin && !isComplete && (
          <div className='rounded-lg border border-utility-warning-200 bg-utility-warning-50 p-4'>
            <p className='text-sm text-utility-warning-700'>
              HubSpot can only be connected by an account admin.
            </p>
          </div>
        )}

        {orgId !== undefined && (
          <>
            {isComplete && <ConnectionStatusPanel orgId={orgId} />}
            {isComplete && isAdmin && (
              <div className='card-refined p-6'>
                <PipelineStageMappingManager orgId={orgId} />
              </div>
            )}
            {!isComplete &&
              isAdmin &&
              connected &&
              // Only a 'sync' connection presents the activation/setup flow. An
              // 'import_only' connection - or a payload missing `purpose` (older
              // backend) - fails closed to connection status, never the setup
              // panel that starts ongoing two-way sync.
              (status.data?.purpose === 'sync' ? (
                <HubspotSetupPanel orgId={orgId} onFinished={handleFinished} />
              ) : (
                <ConnectionStatusPanel orgId={orgId} />
              ))}
            {!isComplete && isAdmin && !connected && (
              <ConnectHubspotButton orgId={orgId} />
            )}
          </>
        )}
      </div>
    </div>
  );
}
